import { CalendarWrapper, ColumnContainer, LeftColumn, RightColumn, ScaleWrapper } from './booking-form.style'
import { BookingAction, BookingPageState, bookingPageDefaultState, pageStateReducer } from '../booking-page/booking-page'
import { PageHeader } from 'old/components/page-header/page-header'
import { RightOutlined } from '@ant-design/icons'
import { Loading, PageContentWrapper, PageError, VerticalContainer } from 'old/components/styled'
import { isNil, useTranslate , min, useWindowDimensions, TranslateFunction, getTimeFromDateTimeString, useElementScrollBreak }  from 'old/utils'
import { useEffect, useReducer, useRef } from 'react'
import { BookingCalendarNavigation, setStartAndEnd } from 'old/components/booking/booking-calendar-navigation/booking-calendar-navigation'
import { BookingCustomer } from 'old/components/booking/booking-customer/booking-customer'
import { BookingService, ServicePreset } from 'old/components/booking/booking-service/booking-service'
import { BookingTeamMembers } from 'old/components/booking/booking-team-members/booking-team-members'
import { FormSection } from 'old/components/form-section/form-section'
import { Scale } from 'old/components/calendar/scale/scale'
import { theme } from 'antd'
import { BookingCalendar } from 'old/components/booking/booking-calendar/booking-calendar'
import { Appointment, Config }  from 'old/types'
import { useConfig, useCreateDraftOrder, useRescheduleAppointment, useSettings } from 'old/api'
import { BOOKING_SCALE_LSK, BOOKING_DEFAULT_SCALE } from 'app.constants'

type Props = {
    servicePreset: ServicePreset
    appointment?: Appointment
    onSave: (bookingId?: string) => void
    onBack: () => void
}

export const BookingForm = ({ servicePreset, appointment, onSave, onBack }: Props) => {

    //hooks:
    const { token: { paddingXXS, padding, paddingXL, margin, marginLG } } = theme.useToken()
    const { width, SM } = useWindowDimensions()
    const { __ } = useTranslate()
    const { data: config, isLoading: isConfigLoading } = useConfig()
    const { data: settings, isLoading: isSettingsLoading } = useSettings()

    //isReschedule:
    const isReschedule = !isNil(appointment)

    //reducer:
    const [ pageState, pageStateDispatch ] = useReducer(pageStateReducer, {...bookingPageDefaultState, scale: getScaleFromLocalStorage()})

    //ref:
    const calendarContainer = useRef<HTMLInputElement>(null)

    //window width:
    useEffect(() => {
        if(calendarContainer.current !== null){
            const calendarContainerWidth = calendarContainer.current.offsetWidth
            const columnsNumber = min(Math.floor(calendarContainerWidth / 200), 7)
            if(columnsNumber !== pageState.numberOfDays){

                //number of days:
                pageStateDispatch({ type: BookingAction.SET_NUMBER_OF_DAYS, payload: { numberOfDays: columnsNumber } })
                
                //end date:
                setStartAndEnd(pageState.startDate, columnsNumber, pageStateDispatch)

            }
        }
    }, [width, calendarContainer.current, settings])

    //save draft:
    const { mutate: createDraft, isLoading: isCreateDraftLoading, data: createDraftResponse } = useCreateDraftOrder(
        pageState.service?.id ?? '',
        pageState.variant?.id ?? '',
        pageState.participants,
        pageState.teamMember?.id ?? '',
        pageState.customer?.id ?? '',
        new Date(pageState.slot?.startDateTime ?? '1991-04-18 00:00'),
        getTimeFromDateTimeString(pageState.slot?.startDateTime ?? '') ?? ''
    )

    //reschedule
    const { mutate: reschedule, isLoading: isRescheduleLoading, data: rescheduleResponse } = useRescheduleAppointment(
        appointment?.id ?? '',
        pageState.teamMember?.id ?? '',
        pageState.slot?.startDateTime ?? ''
    )

    //handleMainButtonClick:
    const handleMainButtonClick = () => {
        if(isReschedule){
            reschedule()
        }else{
            createDraft()
        }
    }

    //handle crate draft response:
    useEffect(() => {createDraftResponse && onSave(createDraftResponse)}, [isCreateDraftLoading])

    //handle reschedule response:
    useEffect(() => {rescheduleResponse && onSave()}, [isRescheduleLoading])
    useEffect(() => {
        if(appointment){
            pageStateDispatch({ type: BookingAction.SET_START_DATE, payload: { startDate: new Date(appointment.datetime) } })
        }
    }, [appointment])

    //handleScaleChange:
    const handleScaleChange = (scale: number) => {
        pageStateDispatch({ type: BookingAction.SET_SCALE, payload: { scale: scale } })
        saveScaleToLocalStorage(scale)
    }
    
    //render:
    return (
        (isConfigLoading || isSettingsLoading) ? <Loading/> :
        (!config || !settings) ? <PageError/> : (

            <PageContentWrapper fullWidth noBottomPadding>

                <VerticalContainer style={{ gap: margin }}>

                    {/* header */}
                    <div> {/* stops the header to be sticky on tablet */}
                        <PageHeader
                            title={isReschedule ? __`reschedule_reassign` : __`booking`}
                            description={isReschedule ? __`change_your_appointment_time_or_team_member` : __`book_an_appointment`}
                            primaryButton={{
                                name: getButtonLabel(config, pageState, __),
                                icon: (pageState.slot && pageState.customer) && <RightOutlined/>,
                                style: {
                                    display: 'flex',
                                    flexDirection: 'row-reverse',
                                    alignItems: 'center'
                                },
                                mode: (!pageState.slot || !pageState.customer) ? 'disabled' : (isCreateDraftLoading || isRescheduleLoading) ? 'loading' : 'normal',
                                onClick: handleMainButtonClick
                            }}
                            backButton={{
                                name: __`back`,
                                onClick: onBack
                            }}
                            isFormDirty={!isNil(pageState.slot)}
                            zIndex={SM ? undefined : 1}
                        />
                    </div>

                    <VerticalContainer style={{ padding: SM ? `0 ${padding}px` : 0, gap: marginLG }}>

                        <ColumnContainer>

                            {/* left column */}
                            <LeftColumn>

                                {/* service */}
                                <BookingService servicePreset={servicePreset} pageState={pageState} pageStateDispatch={pageStateDispatch}/>

                                {/* team member */}
                                <BookingTeamMembers pageState={pageState} pageStateDispatch={pageStateDispatch}/>

                                {/* customer */}
                                <BookingCustomer
                                    pageState={pageState}
                                    pageStateDispatch={pageStateDispatch}
                                    customerPreset={appointment?.customer ?? null}
                                />

                            </LeftColumn>

                            {/* right column */}
                            <RightColumn>

                                <FormSection style={{
                                    paddingTop: paddingXXS,
                                    paddingBottom: SM ? paddingXXS : 0
                                }}>

                                    <CalendarWrapper ref={calendarContainer} style={{ gap: 0 }}>

                                        {/* calendar navigation */}
                                        <BookingCalendarNavigation pageState={pageState} pageStateDispatch={pageStateDispatch}/>
                                    
                                        {/* calendar */}
                                        {pageState.numberOfDays === 0 ? <Loading style={{ padding: paddingXL }}/> : (
                                            <BookingCalendar
                                                settings={settings}
                                                pageState={pageState}
                                                pageStateDispatch={pageStateDispatch}
                                            />
                                        )}

                                    </CalendarWrapper>

                                    {/* scale */}
                                    <BookingFormScale
                                        scale={pageState.scale}
                                        isFormDirty={!isNil(pageState.slot)} 
                                        onScaleChange={handleScaleChange}
                                    ></BookingFormScale>

                                </FormSection>

                            </RightColumn>

                        </ColumnContainer>

                    </VerticalContainer>
                
                </VerticalContainer>

            </PageContentWrapper>
            
        )
    )

}

type BookingFormScaleProps = {
    scale: number
    isFormDirty: boolean
    onScaleChange: (scale: number) => void
}

const BookingFormScale = ({ scale, isFormDirty, onScaleChange }: BookingFormScaleProps) => {
    
    //hooks:
    const { LG } = useWindowDimensions()
    const { isScrollBreak: isLayoutScrollOver75 } = useElementScrollBreak('layoutScrollContainer', 75)

    return (
        <ScaleWrapper bottom={(LG && isFormDirty && isLayoutScrollOver75) ? 57 : 0}>
            <Scale
                scale={scale}
                onScaleChange={onScaleChange}
            />
        </ScaleWrapper>
    )
    
}

//getButtonLabel
const getButtonLabel = (config: Config, pageState: BookingPageState, __: TranslateFunction) => {
    if(pageState.slot === null){
        return __`choose_a_slot`
    }else if(pageState.customer === null){
        return __`choose_a_customer`
    }else{
        const date = new Date(pageState.slot.startDateTime)
        return date.toLocaleString([config.locale, 'en-US'], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    }
}

//getScaleFromLocalStorage:
const getScaleFromLocalStorage = () => {
    let scale = BOOKING_DEFAULT_SCALE
    try{
        const scaleString = localStorage.getItem(BOOKING_SCALE_LSK)
        if(scaleString){
            scale = parseFloat(scaleString)
        }
    }catch(e){
        scale = BOOKING_DEFAULT_SCALE
    }
    return scale ? scale : BOOKING_DEFAULT_SCALE
}

//saveScaleToLocalStorage:
const saveScaleToLocalStorage = (scale: number) => {
    try{
        localStorage.setItem(BOOKING_SCALE_LSK, scale.toString())
    }catch(e){e}
}
